import type { AxiosError, AxiosResponse } from 'axios';

import { apiClient } from './client';

/** One entry from GET /organizations/{id}/users/roles (OrganizationUserRole::catalog()). */
export interface OrganizationUserRoleOption {
  value: string;
  display_name: string;
  description: string;
}

/**
 * Mirrors {@link OrganizationUserResource} on the API. Endpoints are gated
 * server-side by the organization's type === 'command'.
 */
export interface OrganizationUser {
  id: string;
  user_name: string;
  auth_provider_user_id: string | null;
  display_name: string;
  given_name: string | null;
  family_name: string | null;
  type: 'SCIM' | 'Command';
  org_role: string | null;
  deactivated_at: string | null;
  linked_shops: LinkedShop[];
}
export interface LinkedShop {
  id: number;
  name: string | null;
  shop_user_id: number;
}

export interface OrganizationUserCreatePayload {
  user_name: string;
  given_name: string;
  family_name: string;
  display_name?: string | null;
  org_role: string;
  auth_provider_user_id?: string | null;
}

export interface OrganizationUserUpdatePayload {
  given_name: string;
  family_name: string;
  display_name?: string | null;
  org_role: string;
  auth_provider_user_id?: string | null;
}

/** Structured 422 body returned by attach when the shop has no matching User. */
export interface UserNotFoundOnShopError {
  error: 'user_not_found_on_shop';
  message: string;
  shop_id: number;
  user_name: string;
}

/**
 * Structured 422 body emitted by attach / detach when the requested shop
 * exists but is not in `organization_shops` for this organization. In normal
 * flow the modal's picker is already scoped to org-attached shops, so this
 * fires only on stale UI state (someone removed the org-shop link in another
 * tab) or on stale `linked_shops` rows persisted before the API began
 * enforcing membership.
 */
export interface ShopNotInOrganizationError {
  error: 'shop_not_in_organization';
  message: string;
  shop_id: number;
  organization_id: number;
}

export function getOrganizationUsers (organizationId: number | string) {
  return apiClient.get<{ data: OrganizationUser[] }>(
    `/organizations/${organizationId}/users`,
  );
}

export function getOrganizationUserRoles (organizationId: number | string) {
  return apiClient.get<{ data: OrganizationUserRoleOption[] }>(
    `/organizations/${organizationId}/users/roles`,
  );
}

export function createOrganizationUser (
  organizationId: number | string,
  payload: OrganizationUserCreatePayload,
) {
  return apiClient.post<{ data: OrganizationUser }>(
    `/organizations/${organizationId}/users`,
    payload,
  );
}

export function updateOrganizationUser (
  organizationId: number | string,
  userUuid: string,
  payload: OrganizationUserUpdatePayload,
) {
  return apiClient.put<{ data: OrganizationUser }>(
    `/organizations/${organizationId}/users/${userUuid}`,
    payload,
  );
}

export function deleteOrganizationUser (
  organizationId: number | string,
  userUuid: string,
) {
  return apiClient.delete(`/organizations/${organizationId}/users/${userUuid}`);
}

/**
 * Structured 422 body returned when the user has already accepted their
 * invitation (`auth_provider_user_id` is set), so there is nothing to resend.
 */
export interface UserAlreadyProvisionedError {
  error: 'user_already_provisioned';
  message: string;
}

/**
 * Re-dispatch the Auth0 invitation email for a pending organization user
 * (one with no `auth_provider_user_id` yet). Resolves with the refreshed user.
 *
 * 422 `error: 'user_already_provisioned'` — the user already accepted their
 * invite. Use {@link isUserAlreadyProvisionedError} to surface a clear message.
 */
export function resendOrganizationUserInvite (
  organizationId: number | string,
  userUuid: string,
): Promise<AxiosResponse<{ data: OrganizationUser; message: string }>> {
  return apiClient.post(
    `/organizations/${organizationId}/users/${userUuid}/resend-invite`,
  );
}

/**
 * Bind an OrganizationUser to a shop's User identity (existing User required).
 *
 * 422 paths the caller must distinguish:
 * - `error: 'user_not_found_on_shop'` — shop is in the org but has no
 *   matching User row. Use {@link isUserNotFoundOnShopError}; prompt the
 *   operator to create the shop-side user first.
 * - `error: 'shop_not_in_organization'` — shop is not attached to the org
 *   at all (stale picker or race). Use {@link isShopNotInOrganizationError};
 *   prompt the operator to refresh and pick from the current shop list.
 */
export function attachUserShop (
  organizationId: number | string,
  userUuid: string,
  shopId: number | string,
): Promise<AxiosResponse<{ data: OrganizationUser; message: string }>> {
  return apiClient.post(
    `/organizations/${organizationId}/users/${userUuid}/shops/${shopId}`,
  );
}

/** Idempotent. Response body includes refreshed `linked_shops` and `removed_count`. */
export function detachUserShop (
  organizationId: number | string,
  userUuid: string,
  shopId: number | string,
): Promise<AxiosResponse<{ data: OrganizationUser; message: string; removed_count: number }>> {
  return apiClient.delete(
    `/organizations/${organizationId}/users/${userUuid}/shops/${shopId}`,
  );
}

export function isUserNotFoundOnShopError (
  error: unknown,
): error is AxiosError<UserNotFoundOnShopError> {
  const axiosError = error as AxiosError<{ error?: string }> | undefined;
  return (
    axiosError?.response?.status === 422 &&
    axiosError?.response?.data?.error === 'user_not_found_on_shop'
  );
}

export function isShopNotInOrganizationError (
  error: unknown,
): error is AxiosError<ShopNotInOrganizationError> {
  const axiosError = error as AxiosError<{ error?: string }> | undefined;
  return (
    axiosError?.response?.status === 422 &&
    axiosError?.response?.data?.error === 'shop_not_in_organization'
  );
}

export function isUserAlreadyProvisionedError (
  error: unknown,
): error is AxiosError<UserAlreadyProvisionedError> {
  const axiosError = error as AxiosError<{ error?: string }> | undefined;
  return (
    axiosError?.response?.status === 422 &&
    axiosError?.response?.data?.error === 'user_already_provisioned'
  );
}
